import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { getConfig } from '@/config/env';

import { EmbedContext, type EmbedContextValue } from './embed-context';
import { isEmbedded, pickEmbedParams, readEmbedParams, type EmbedParams } from './embed-params';

/** Message the host page may send to update the embed parameters at runtime. */
const HOST_PARAMS_MESSAGE = 'mdenture:params';

export interface EmbedProviderProps {
  children: ReactNode;
  /** Test seam: overrides the auto-detected iframe state. */
  embedded?: boolean;
  /** Test seam: overrides the query string the initial params are read from. */
  search?: string;
}

/**
 * Bridges the embedding host page and the app.
 *
 * Parameters arrive two ways:
 *  1. Query string on the iframe `src` — read once at mount.
 *  2. `postMessage` from the host — merged in whenever it arrives, so the PHP
 *     page can update the embed without reloading the iframe.
 *
 * SECURITY: every incoming message is rejected unless `event.origin` is in the
 * configured `VITE_ALLOWED_PARENT_ORIGINS` allowlist, and outgoing messages are
 * always addressed to a concrete origin — never '*'.
 */
export function EmbedProvider({ children, embedded, search }: EmbedProviderProps): ReactNode {
  const allowedOrigins = useMemo(() => getConfig().allowedParentOrigins, []);
  const framed = embedded ?? isEmbedded();

  const [params, setParams] = useState<EmbedParams>(() => readEmbedParams(search ?? window.location.search));

  useEffect(() => {
    if (!framed || allowedOrigins.length === 0) {
      return;
    }

    function onMessage(event: MessageEvent): void {
      if (!allowedOrigins.includes(event.origin)) {
        return; // Untrusted sender — ignore silently.
      }
      const data = event.data as { type?: unknown; params?: unknown } | null;
      if (!data || data.type !== HOST_PARAMS_MESSAGE || typeof data.params !== 'object' || !data.params) {
        return;
      }
      const incoming = pickEmbedParams(data.params as Record<string, unknown>);
      setParams((current) => ({ ...current, ...incoming }));
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [framed, allowedOrigins]);

  const postToHost = useCallback<EmbedContextValue['postToHost']>(
    (message) => {
      if (!framed) {
        return;
      }
      for (const origin of allowedOrigins) {
        window.parent.postMessage(message, origin);
      }
    },
    [framed, allowedOrigins],
  );

  const value = useMemo<EmbedContextValue>(
    () => ({ embedded: framed, params, postToHost }),
    [framed, params, postToHost],
  );

  return <EmbedContext.Provider value={value}>{children}</EmbedContext.Provider>;
}
